import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  collection, doc, getDoc, getDocs,
  query, where, addDoc, updateDoc, setDoc, serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const GroupContext = createContext();
export function useGroup() { return useContext(GroupContext); }

export function GroupProvider({ children }) {
  const { currentUser, userData } = useAuth();
  const [currentGroup, setCurrentGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [allPredictions, setAllPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGroupId, setActiveGroupId] = useState(
    () => localStorage.getItem('activeGroupId') || null
  );

  useEffect(() => {
    if (!userData) { setLoading(false); return; }
    const gid = activeGroupId || userData?.groupId;
    if (!gid) { setLoading(false); return; }
    loadAll(gid);
  }, [userData, activeGroupId]);

  async function loadAll(gid) {
  const activeGid = gid || localStorage.getItem('activeGroupId') || userData?.groupId;
  setLoading(true);
  try {
    // VALIDACIÓN: Asegurar que tenemos groupId
    if (!activeGid) {
      console.log('No hay groupId en userData:', userData);
      setLoading(false);
      return;
    }

    // Grupo
    const groupSnap = await getDoc(doc(db, 'groups', activeGid));
    if (groupSnap.exists()) {
      setCurrentGroup({ id: groupSnap.id, ...groupSnap.data() });
    } else {
      console.error('Grupo no encontrado:', userData.groupId);
      setLoading(false);
      return;
    }

    // Miembros del grupo (subcolección) - listener en tiempo real
    try {
      const membersRef = collection(db, 'groups', activeGid, 'members');
      const unsubMembers = onSnapshot(membersRef, (snap) => {
        setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (e) => {
        console.log('Error cargando miembros (puede ser nuevo):', e);
        setMembers([]);
      });
      // Guardar unsub para limpiar en siguiente loadAll
      window._pitazoMembersUnsub && window._pitazoMembersUnsub();
      window._pitazoMembersUnsub = unsubMembers;
    } catch (e) {
      console.log('Error cargando miembros (puede ser nuevo):', e);
      setMembers([]);
    }

    // Fixtures ordenados por número de partido - listener en tiempo real
    try {
      const fixturesRef = collection(db, 'fixtures');
      const unsubFixtures = onSnapshot(fixturesRef, (snap) => {
        const fixturesList = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.matchNumber || a.n || 0) - (b.matchNumber || b.n || 0));
        setFixtures(fixturesList);
      }, (e) => {
        console.error('Error cargando fixtures:', e);
        setFixtures([]);
      });
      window._pitazoFixturesUnsub && window._pitazoFixturesUnsub();
      window._pitazoFixturesUnsub = unsubFixtures;
    } catch (e) {
      console.error('Error cargando fixtures:', e);
      setFixtures([]);
    }

    // Predicciones del usuario actual
    await loadMyPredictions();

  } catch (e) { 
    console.error('Error cargando datos:', e);
  }
  setLoading(false);
}

  async function loadMyPredictions() {
    if (!currentUser) return;
    try {
      const activeGid = localStorage.getItem('activeGroupId') || userData?.groupId;
      const q = query(
        collection(db, 'predictions'),
        where('userId', '==', currentUser.uid),
        where('groupId', '==', activeGid)
      );
      const snap = await getDocs(q);
      setPredictions(snap.docs.map(d => ({ id: d.id, ...d.data() })));

      // También cargar TODAS las predicciones del grupo (para ver las de otros cuando cierra)
      if (activeGid) {
        const qAll = query(
          collection(db, 'predictions'),
          where('groupId', '==', activeGid)
        );
        const snapAll = await getDocs(qAll);
        setAllPredictions(snapAll.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (e) { console.error('Error cargando predicciones:', e); }
  }

  // ── GUARDAR PREDICCIÓN ───────────────────────────────
  async function savePrediction(fixtureId, homeScore, awayScore) {
    if (!currentUser || !userData?.groupId) return false;

    const predData = {
      userId: currentUser.uid,
      groupId: localStorage.getItem('activeGroupId') || userData?.groupId,
      fixtureId,
      homeScore: parseInt(homeScore),
      awayScore: parseInt(awayScore),
      updatedAt: serverTimestamp(),
    };

    try {
      // Buscar predicción existente
      const existing = predictions.find(p => p.fixtureId === fixtureId);

      if (existing) {
        await updateDoc(doc(db, 'predictions', existing.id), predData);
        // Actualizar estado local
        setPredictions(prev => prev.map(p =>
          p.id === existing.id ? { ...p, ...predData } : p
        ));
      } else {
        const ref = await addDoc(collection(db, 'predictions'), {
          ...predData,
          createdAt: serverTimestamp(),
        });
        // Agregar al estado local
        setPredictions(prev => [...prev, { id: ref.id, ...predData }]);
      }
      return true;
    } catch (e) {
      console.error('Error guardando predicción:', e);
      return false;
    }
  }

  // ── TABLA DE POSICIONES ──────────────────────────────
  function calculateLeaderboard() {
    const group = currentGroup;
    const rules = group?.pointRules || { exactScore: 5, winner: 1, goalDiff: 2 };

    const scores = {};
    members.forEach(m => {
      scores[m.userId || m.id] = {
        userId: m.userId || m.id,
        name: m.name,
        points: m.totalPoints || 0,
        exact: m.exactPredictions || 0,
        correct: m.correctPredictions || 0,
      };
    });

    return Object.values(scores).sort((a, b) => b.points - a.points || b.exact - a.exact);
  }

  // Cambiar de grupo activo
  function switchGroup(gid) {
    localStorage.setItem('activeGroupId', gid);
    setActiveGroupId(gid);
  }

  return (
    <GroupContext.Provider value={{
      currentGroup, members, fixtures, predictions,
      loading, savePrediction, calculateLeaderboard, allPredictions,
      reload: loadAll, switchGroup, activeGroupId
    }}>
      {children}
    </GroupContext.Provider>
  );
}